import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { colors } from '../../../constants/colors';
import {
  WEB_ANALYTICS_COLORS,
  buildYTicks,
  formatTrendLabel,
  shouldShowXTick,
} from '../../../constants/webAnalyticsTheme';

/**
 * Traffic over time.
 * data rows: { date, page_views, sessions?, unique_visitors?, events? }
 */
const TrafficTrendChart = ({
  data = [],
  isLoading = false,
  metric = 'page_views',
  days = 30,
}) => {
  const chartData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        label: formatTrendLabel(item.date, days),
        page_views: item.page_views || 0,
        sessions: item.sessions || 0,
        unique_visitors: item.unique_visitors || 0,
        events: item.events || 0,
      })),
    [data, days]
  );

  const metricLabel = {
    page_views: 'Page Views',
    sessions: 'Sessions',
    unique_visitors: 'Visitors',
    events: 'Events',
  };

  const stroke =
    WEB_ANALYTICS_COLORS[metric] || WEB_ANALYTICS_COLORS.page_views;

  const { domainMax, ticks } = useMemo(() => {
    const maxVal = Math.max(...chartData.map((r) => Number(r[metric]) || 0), 0);
    return buildYTicks(maxVal, 5);
  }, [chartData, metric]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '280px',
          color: colors.text.secondary,
        }}
      >
        Loading...
      </div>
    );
  }

  const hasData = chartData.some((row) => (row[metric] || 0) > 0);

  if (!chartData.length || !hasData) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '280px',
          color: colors.text.secondary,
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <p style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
          No traffic yet
        </p>
        <p style={{ margin: '6px 0 0', fontSize: '13px' }}>
          Daily page views will appear once visitors hit your site
        </p>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    return (
      <div
        style={{
          backgroundColor: colors.background.primary,
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '10px 12px',
          boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13, color: colors.text.primary }}>
          {label}
        </div>
        <div style={{ fontSize: 12, color: WEB_ANALYTICS_COLORS.page_views, marginTop: 4 }}>
          Page Views: {row.page_views || 0}
        </div>
        <div style={{ fontSize: 12, color: WEB_ANALYTICS_COLORS.sessions }}>
          Sessions: {row.sessions || 0}
        </div>
        <div style={{ fontSize: 12, color: WEB_ANALYTICS_COLORS.unique_visitors }}>
          Visitors: {row.unique_visitors || 0}
        </div>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={chartData} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={colors.borderVariants.light} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: colors.text.secondary }}
          tickLine={false}
          axisLine={{ stroke: colors.borderVariants.light }}
          interval={0}
          tickFormatter={(value, index) =>
            shouldShowXTick(index, chartData.length, days) ? value : ''
          }
        />
        <YAxis
          domain={[0, domainMax]}
          ticks={ticks}
          allowDecimals={false}
          tick={{ fontSize: 11, fill: colors.text.secondary }}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: 12, color: colors.text.secondary }}
          formatter={() => metricLabel[metric] || 'Page Views'}
        />
        <Line
          type="monotone"
          dataKey={metric}
          name={metricLabel[metric] || 'Page Views'}
          stroke={stroke}
          strokeWidth={2.5}
          dot={chartData.length <= 14}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default TrafficTrendChart;
